/**
 * Freeze V2 — Data fetching hook.
 *
 * Manages loading fertilizations and embryos via tanstack-query.
 * Embryo data is fetched after fertilizations load and is keyed by fertId.
 */
import { useCallback, useEffect, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
  UpdateApprovalNumberRequest,
} from '@/services/freeze';

import {
  fetchFertAndEmbryos,
  fetchFertilizationListFreeze,
  useApplyApprovalNumbersMutation,
} from '../services/freezeApi';

type UseFreezeDataParams = {
  fertilizationDate: string;
  fertilizationSiteId: string | null;
  searchQuery?: string;
};

type UseFreezeDataReturn = {
  fertilizations: FertilizationListFreezeResponse[];
  embryosByFert: Map<string, FertAndEmbryosResponse>;
  isLoadingFerts: boolean;
  isLoadingEmbryos: boolean;
  refetchAll: () => void;
  refetchEmbryos: () => void;
};

const FERT_QUERY_KEY = 'freeze-v2-fertilizations';
export const EMBRYO_QUERY_KEY = 'freeze-v2-embryos';

export function useFreezeData({
  fertilizationDate,
  fertilizationSiteId,
  searchQuery,
}: UseFreezeDataParams): UseFreezeDataReturn {
  const queryClient = useQueryClient();

  const enabled = !!fertilizationDate && !!fertilizationSiteId;

  const fertsQuery = useQuery({
    queryKey: [
      FERT_QUERY_KEY,
      fertilizationDate,
      fertilizationSiteId,
      searchQuery,
    ],
    queryFn: () =>
      fetchFertilizationListFreeze({
        fertilizationDate,
        fertilizationSiteId: fertilizationSiteId!,
        searchQuery: searchQuery || undefined,
      }),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const fertilizationIds = useMemo(
    () =>
      (fertsQuery.data ?? [])
        .map((f) => f.fertId)
        .filter((id): id is string => !!id),
    [fertsQuery.data],
  );

  const embryosQuery = useQuery({
    queryKey: [
      EMBRYO_QUERY_KEY,
      fertilizationDate,
      fertilizationSiteId,
      fertilizationIds,
    ],
    queryFn: () =>
      fetchFertAndEmbryos({
        fertilizationDate,
        fertilizationSiteId: fertilizationSiteId!,
        fertilizationIds,
      }),
    enabled: enabled && fertilizationIds.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const embryosByFert = useMemo(() => {
    const map = new Map<string, FertAndEmbryosResponse>();
    for (const entry of embryosQuery.data ?? []) {
      map.set(entry.fertId, entry);
    }
    return map;
  }, [embryosQuery.data]);

  // Auto-apply approval numbers to live embryos once data loads (parity with v1 useFreeze).
  const { mutate: applyApprovalNumbers } = useApplyApprovalNumbersMutation();
  useEffect(() => {
    const ferts = fertsQuery.data;
    if (!ferts || !embryosQuery.data) return;

    const updates: UpdateApprovalNumberRequest[] = [];
    for (const fert of ferts) {
      const fertId = fert.fertId;
      const approvalNumberId = fert.approvalNumberId ?? null;
      if (!fertId || !approvalNumberId) continue;

      const row = embryosByFert.get(fertId);
      if (!row?.liveEmbryos?.length) continue;

      const embryoIds = Array.from(
        new Set(
          row.liveEmbryos.map((e) => e.id).filter((id): id is string => !!id),
        ),
      );
      if (embryoIds.length === 0) continue;

      updates.push({ fertId, approvalNumberId, embryoIds });
    }

    if (updates.length > 0) applyApprovalNumbers(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fertsQuery.data, embryosQuery.data]);

  const refetchAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [FERT_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [EMBRYO_QUERY_KEY] });
  }, [queryClient]);

  const refetchEmbryos = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [EMBRYO_QUERY_KEY] });
  }, [queryClient]);

  return {
    fertilizations: fertsQuery.data ?? [],
    embryosByFert,
    isLoadingFerts: fertsQuery.isLoading,
    isLoadingEmbryos: embryosQuery.isLoading,
    refetchAll,
    refetchEmbryos,
  };
}
